import React, { useEffect, useRef, useState } from 'react'
import { acquireCameraStream, stopCameraStream } from './cameraConfig'

// Average an N×N window around the tapped point so a single noisy pixel doesn't
// decide the colour.
const SAMPLE = 7

// Live camera eyedropper: opens the configured camera, lets the user tap the
// colour they want straight off the video, and hands back that [r,g,b]. The
// camera is released as soon as the picker closes (privacy 18.3 — the camera
// must not stay live when it isn't being used).
const CameraColorPicker = ({ onPick, onClose }) => {
    const videoRef = useRef(null)
    const streamRef = useRef(null)
    const canvasRef = useRef(null)
    const [ready, setReady] = useState(false)
    const [error, setError] = useState('')
    const [preview, setPreview] = useState(null)

    useEffect(() => {
        let cancelled = false
        const start = async () => {
            try {
                const deviceId =
                    sessionStorage.getItem('aiCameraDeviceId') || ''
                const stream = await acquireCameraStream(deviceId)
                if (cancelled) {
                    stopCameraStream(stream)
                    return
                }
                streamRef.current = stream
                if (videoRef.current) {
                    videoRef.current.srcObject = stream
                    await videoRef.current.play().catch(() => { })
                }
                setReady(true)
            } catch (e) {
                setError('Camera unavailable')
            }
        }
        start()
        return () => {
            cancelled = true
            if (streamRef.current) stopCameraStream(streamRef.current)
            streamRef.current = null
        }
    }, [])

    // Map a click on the (object-fit: contain) video to the frame pixel and
    // average the small window there.
    const sampleAt = (clientX, clientY) => {
        const v = videoRef.current
        if (!v || !v.videoWidth) return null
        const rect = v.getBoundingClientRect()
        const vAspect = v.videoWidth / v.videoHeight
        const rAspect = rect.width / rect.height
        let dispW
        let dispH
        let offX
        let offY
        if (rAspect > vAspect) {
            dispH = rect.height
            dispW = dispH * vAspect
            offX = (rect.width - dispW) / 2
            offY = 0
        } else {
            dispW = rect.width
            dispH = dispW / vAspect
            offX = 0
            offY = (rect.height - dispH) / 2
        }
        const px = clientX - rect.left - offX
        const py = clientY - rect.top - offY
        if (px < 0 || py < 0 || px > dispW || py > dispH) return null
        const sx = Math.floor((px / dispW) * v.videoWidth)
        const sy = Math.floor((py / dispH) * v.videoHeight)
        if (!canvasRef.current) canvasRef.current = document.createElement('canvas')
        const c = canvasRef.current
        c.width = v.videoWidth
        c.height = v.videoHeight
        const ctx = c.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(v, 0, 0)
        const half = Math.floor(SAMPLE / 2)
        const x0 = Math.max(0, sx - half)
        const y0 = Math.max(0, sy - half)
        const w = Math.min(SAMPLE, v.videoWidth - x0)
        const h = Math.min(SAMPLE, v.videoHeight - y0)
        const data = ctx.getImageData(x0, y0, w, h).data
        let r = 0
        let g = 0
        let b = 0
        let n = 0
        for (let i = 0; i < data.length; i += 4) {
            r += data[i]
            g += data[i + 1]
            b += data[i + 2]
            n++
        }
        if (!n) return null
        return [Math.round(r / n), Math.round(g / n), Math.round(b / n)]
    }

    const onTap = (e) => {
        const rgb = sampleAt(e.clientX, e.clientY)
        if (rgb) setPreview(rgb)
    }

    const confirm = () => {
        if (preview) onPick(preview)
    }

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
                <p style={styles.title}>Tap the colour you want</p>
                <div style={styles.stage}>
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        style={styles.video}
                        onClick={onTap}
                    />
                    {!ready && !error && (
                        <div style={styles.hint}>Starting camera…</div>
                    )}
                    {error && <div style={styles.hint}>{error}</div>}
                </div>
                <div style={styles.footer}>
                    {preview ? (
                        <div style={styles.previewRow}>
                            <span
                                style={{
                                    ...styles.swatch,
                                    background: `rgb(${preview.join(',')})`,
                                }}
                            />
                            <span style={styles.previewText}>
                                Picked this colour
                            </span>
                        </div>
                    ) : (
                        <span style={styles.previewText}>
                            Point the camera and tap a colour
                        </span>
                    )}
                    <div style={styles.btns}>
                        <button style={styles.btnGhost} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            style={{
                                ...styles.btnPrimary,
                                opacity: preview ? 1 : 0.5,
                                cursor: preview ? 'pointer' : 'not-allowed',
                            }}
                            onClick={confirm}
                            disabled={!preview}
                        >
                            Use colour
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 199999,
    },
    panel: {
        background: '#fff',
        borderRadius: 16,
        padding: 18,
        width: 'min(90vw, 480px)',
        boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
    },
    title: {
        margin: '0 0 12px',
        fontFamily: 'Halcyon_SemiBold',
        fontSize: 18,
        color: '#1b2430',
        textAlign: 'center',
    },
    stage: {
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        background: '#111',
        borderRadius: 12,
        overflow: 'hidden',
    },
    video: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        cursor: 'crosshair',
        display: 'block',
    },
    hint: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: 'Halcyon_Medium',
        fontSize: 15,
    },
    footer: {
        marginTop: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
    },
    previewRow: { display: 'flex', alignItems: 'center', gap: 10 },
    swatch: {
        width: 30,
        height: 30,
        borderRadius: 8,
        border: '2px solid #fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    },
    previewText: {
        fontFamily: 'Halcyon_Medium',
        fontSize: 14,
        color: '#444',
    },
    btns: { display: 'flex', gap: 10, marginLeft: 'auto' },
    btnGhost: {
        border: '1.5px solid #ccc',
        background: 'transparent',
        borderRadius: 20,
        padding: '9px 18px',
        fontFamily: 'Halcyon_SemiBold',
        cursor: 'pointer',
    },
    btnPrimary: {
        border: 'none',
        background: '#f4813f',
        color: '#fff',
        borderRadius: 20,
        padding: '9px 20px',
        fontFamily: 'Halcyon_SemiBold',
    },
}

export default CameraColorPicker
